import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { throwError, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GlobalConstants } from '../../common/global-constants';
import { AuthService } from '../../core/auth/auth.service';
import { ScanStatusResponse } from '../../interfaces/asm/ScanStatusResponse';
import { QueryIpsResponse } from '../../interfaces/asm/QueryIpsResponse';
import { QueryPortsResponse } from '../../interfaces/asm/QueryPortsResponse';
import { QueryTechnologiesResponse } from '../../interfaces/asm/QueryTechnologiesResponse';
import { VisualiseDataResponse } from '../../interfaces/asm/VisualiseDataResponse';
import { environment } from '../../../environments/environment';
import { ListDatatableSubdomainResponse } from '../../interfaces/asm/ListDatatableSubdomainResponse';
import { QuerySubdomainsResponse } from '../../interfaces/asm/QuerySubdomainsResponse';
import { QueryTargetsResponse } from '../../interfaces/asm/QueryTargetsResponse';
import { QueryEndpointsResponse } from '../../interfaces/asm/QueryEndpointsResponse';
import { QueryVulnerabilitiesResponse } from '../../interfaces/asm/QueryVulnerabilitiesResponse';
import { QueryEmailsResponse } from '../../interfaces/asm/QueryEmailsResponse';
import { QueryEmployeesResponse } from '../../interfaces/asm/QueryEmployeesResponse';

@Injectable({
    providedIn: 'root',
})
export class AsmService {
    private _url: string = environment.apiUrlAsm;

    constructor(
        private _httpClient: HttpClient,
        private _authService: AuthService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * getScanStatus
     */
    getScanStatus(project: string): Observable<ScanStatusResponse> {
        let path = `${this._url}/scan_status/?format=json&project=${project}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        return this._httpClient
            .get<ScanStatusResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getQuerySubdomains
     */
    getQuerySubdomains(
        project: string = '',
        scanId: string = '',
        targetId: string = '',
        name: string = '',
        ipAddress: string = '',
        port: string = '',
        tech: string = '',
        onlyImportant: boolean = false,
        noLookupInteresting: boolean = false
    ): Observable<QuerySubdomainsResponse> {
        let path = `${this._url}/querySubdomains/?format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        if (project) {
            path = path + `&project=${project}`;
        }
        if (scanId) {
            path = path + `&scan_id=${scanId}`;
        } else if (targetId) {
            path = path + `&target_id=${targetId}`;
        }
        if (name) {
            path = path + `&name=${name}`;
        }
        if (ipAddress) {
            path = path + `&ip_address=${ipAddress}`;
        }
        if (port) {
            path = path + `&port=${port}`;
        }
        if (tech) {
            path = path + `&tech=${tech}`;
        }
        if (onlyImportant) {
            path = path + `&only_important`;
        }
        if (noLookupInteresting) {
            path = path + `&no_lookup_interesting`;
        }
        return this._httpClient
            .get<QuerySubdomainsResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getListTodoNotes
     */
    getListTodoNotes(scanId: string): Observable<string> {
        let path = `${this._url}/listTodoNotes/?scan_id=${scanId}&format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        return this._httpClient
            .get<string>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getQueryIps
     */
    getQueryIps(scanId: number): Observable<QueryIpsResponse> {
        let path = `${this._url}/queryIps/?scan_id=${scanId}&format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        return this._httpClient
            .get<QueryIpsResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getQueryTechnologies
     */
    getQueryTechnologies(
        scanId: number
    ): Observable<QueryTechnologiesResponse> {
        let path = `${this._url}/queryTechnologies/?scan_id=${scanId}&format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        return this._httpClient
            .get<QueryTechnologiesResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getQueryPorts
     */
    getQueryPorts(scanId: number): Observable<QueryPortsResponse> {
        let path = `${this._url}/queryPorts/?scan_id=${scanId}&format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        return this._httpClient
            .get<QueryPortsResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getQueryAllScanResultVisualise
     */
    getQueryAllScanResultVisualise(
        scanId: number
    ): Observable<VisualiseDataResponse[]> {
        let path = `${this._url}/queryAllScanResultVisualise/?scan_id=${scanId}&format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        return this._httpClient
            .get<VisualiseDataResponse[]>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getQueryTargets
     */
    getQueryTargets(slug: string = ''): Observable<QueryTargetsResponse> {
        let path = `${this._url}/queryTargets/?format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        if (slug) {
            path = path + `&slug=${slug}`;
        }
        return this._httpClient
            .get<QueryTargetsResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getListDatatableSubdomain
     */
    getListDatatableSubdomain(
        project: string,
        scanId: number = 0,
        targetId: string = '',
        name: string = '',
        ipAddress: string = '',
        urlQuery: string = ''
    ): Observable<ListDatatableSubdomainResponse> {
        let path = `${this._url}/listDatatableSubdomain/?project=${project}`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        if (scanId) {
            path = path + `&scan_id=${scanId}`;
        } else if (targetId) {
            path = path + `&target_id=${targetId}`;
        }
        if (name) {
            path = path + `&name=${name}`;
        }
        if (ipAddress) {
            path = path + `&ip_address=${ipAddress}`;
        }
        if (urlQuery) {
            path = path + `&query_param=${urlQuery}`;
        }
        path = path + `&format=json`;
        return this._httpClient
            .get<ListDatatableSubdomainResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getQueryEndpoints
     */
    getQueryEndpoints(
        scanId: string = '',
        targetId: string = '',
        subdomainName: string = '',
        pattern: string = '',
        onlyUrls: boolean = false,
    ): Observable<QueryEndpointsResponse> {
        let path = `${this._url}/queryEndpoints/?format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        if (scanId) {
            path = path + `&scan_id=${scanId}`;
        } else if (targetId) {
            path = path + `&target_id=${targetId}`;
        }
        if (subdomainName) {
            path = path + `&subdomain_name=${subdomainName}`;
        }
        if (pattern) {
            path = path + `&pattern=${pattern}`;
        }
        if (onlyUrls) {
            path = path + `&only_urls`;
        }
        return this._httpClient
            .get<QueryEndpointsResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getQueryVulnerabilities
     */
    getQueryVulnerabilities(
        slug: string = '',
        scanId: string = '',
        targetId: string = '',
        subdomainName: string = '',
        domain: string = '',
        severity: string = '',
        subdomainId: string = '',
        vulnerabilityName: string = '',
    ): Observable<QueryVulnerabilitiesResponse> {
        let path = `${this._url}/queryVulnerabilities/?format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        if (slug) {
            path = path + `&project=${slug}`;
        }
        if (scanId) {
            path = path + `&scan_history=${scanId}`;
        } else if (targetId) {
            path = path + `&target_id=${targetId}`;
        } else if (subdomainName) {
            path = path + `&subdomain=${subdomainName}`;
        }
        if (domain) {
            path = path + `&domain=${domain}`;
        }
        if (severity) {
            path = path + `&severity=${severity}`;
        }
        if (subdomainId) {
            path = path + `&subdomain_id=${subdomainId}`;
        }
        if (vulnerabilityName) {
            path = path + `&vulnerability_name=${vulnerabilityName}`;
        }
        return this._httpClient
            .get<QueryVulnerabilitiesResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getQueryEmails
     */
    getQueryEmails(scanId: number): Observable<QueryEmailsResponse> {
        let path = `${this._url}/queryEmails/?scan_id=${scanId}&format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        return this._httpClient
            .get<QueryEmailsResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    /**
     * getQueryEmployees
     */
    getQueryEmployees(scanId: number): Observable<QueryEmployeesResponse> {
        let path = `${this._url}/queryEmployees/?scan_id=${scanId}&format=json`;
        const headers = new HttpHeaders({
            Authorization: 'Bearer ' + this._authService.asmToken,
        });
        return this._httpClient
            .get<QueryEmployeesResponse>(path, { headers })
            .pipe(catchError(this.handleError));
    }

    //POST https://asm.intrustservices.co/api/listSubScans/?format=json

    //POST https://asm.intrustservices.co/api/fetch/most_vulnerable/?format=json

    //POST https://asm.intrustservices.co/api/fetch/most_common_vulnerability/?format=json

    // GET https://asm.intrustservices.co/api/queryAllScanResultVisualise/?scan_id=25&format=json

    // -----------------------------------------------------------------------------------------------------
    // handleError
    // -----------------------------------------------------------------------------------------------------

    handleError(error: any) {
        console.error(error);
        return throwError(() => new Error(error));
    }
}
